import React, { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { ArrowClockwise } from "react-bootstrap-icons";

import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
  createSuccessFeedback,
} from "../../../context/ui-feedback/action";
import { PageLayout } from "../../common/page-layout/page-layout";
import { ControllerRow } from "../../common/controller-row/controller-row";
import {
  useLoadJobs,
  useRunJob,
  useStopStartJob,
  useListJobLogs,
} from "../../../api/scheduled-jobs-api";
import { ScheduledJobTable } from "./components/scheduled-job-table";
import { ScheduledLogTable } from "./components/scheduled-log-table";

export const ScheduledJobs = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const loadJobs = useLoadJobs();
  const runJob = useRunJob();
  const stopStartJob = useStopStartJob();
  const listJobLogs = useListJobLogs();

  const loaded = useRef(null);
  const [jobs, setJobs] = useState(null);
  const [jobLogs, setJobLogs] = useState(null);

  useEffect(() => {
    const loadJobList = async () => {
      try {
        await loadJobs((data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            setJobs(data);
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.scheduledjobs.exception.listjobs.title",
            }),
            intl.formatMessage({
              id: "page.scheduledjobs.exception.listjobs.description",
            }),
          );
        }
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      !loaded.current &&
      !jobs &&
      checkNotEmpy([intl, navigate, loadJobs, uiFeedbackDispatch])
    ) {
      loaded.current = true;
      loadJobList();
    }
  }, [jobs, loaded, intl, navigate, loadJobs, uiFeedbackDispatch]);

  const refreshJobTable = () => {
    loaded.current = null;
    setJobLogs(null);
    setJobs(null);
  };

  const handleDisabled = async (jobId, disabled) => {
    createWaiting(uiFeedbackDispatch);
    try {
      await stopStartJob(jobId, disabled, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          const successMsg = intl.formatMessage({
            id: disabled
              ? "page.scheduledjobs.success.stopJob"
              : "page.scheduledjobs.success.startJob",
          });
          createSuccessFeedback(uiFeedbackDispatch, successMsg);
          loaded.current = null;
          setJobLogs(null);
          setJobs(null);
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        const errorTitle = intl.formatMessage({
          id: disabled
            ? "page.scheduledjobs.exception.stopJob.title"
            : "page.scheduledjobs.exception.startJob.title",
        });
        const errorMs = intl.formatMessage({
          id: disabled
            ? "page.scheduledjobs.exception.stopJob.description"
            : "page.scheduledjobs.exception.startJob.description",
        });
        createErrorFeedback(uiFeedbackDispatch, errorTitle, errorMs);
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
    }
  };

  const handleRun = async (jobId) => {
    createWaiting(uiFeedbackDispatch);
    try {
      await runJob(jobId, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          createSuccessFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.scheduledjobs.success.runJob",
            }),
          );
          loaded.current = null;
          setJobLogs(null);
          setJobs(null);
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.scheduledjobs.exception.runJob.title",
          }),
          intl.formatMessage({
            id: "page.scheduledjobs.exception.runJob.description",
          }),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
    }
  };

  const handleShowLog = async (jobId, page) => {
    const limit = 10;
    setJobLogs({
      id: jobId,
      currentPage: page,
      allPage: 0,
      rowInit: page * limit,
      data: null,
    });
    createWaiting(uiFeedbackDispatch);
    try {
      await listJobLogs(jobId, page, limit, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          setJobLogs({
            id: jobId,
            currentPage: page,
            allPage: Math.ceil(data?.allCount / limit),
            rowInit: page * limit,
            data: data,
          });
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.scheduledjobs.exception.runJob.title",
          }),
          intl.formatMessage({
            id: "page.scheduledjobs.exception.runJob.description",
          }),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
    }
  };

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
    ];
  };

  return (
    <PageLayout
      paths={buildPaths()}
      title={intl.formatMessage({ id: "page.scheduledjobs.title" })}
    >
      <>
        <ControllerRow>
          <div className="text-end">
            <Button variant="outline-secondary" onClick={refreshJobTable}>
              <ArrowClockwise />
            </Button>
          </div>
        </ControllerRow>
        <ScheduledJobTable
          jobs={jobs}
          handleDisabled={handleDisabled}
          handleRun={handleRun}
          handleShowLog={(jobId) => handleShowLog(jobId, 0)}
        />
        {jobLogs ? (
          <ScheduledLogTable
            job={jobs.find((j) => j.id === jobLogs.id)}
            currentPage={jobLogs.currentPage}
            rowInit={jobLogs.rowInit}
            allPage={jobLogs.allPage}
            logs={jobLogs?.data?.logs}
            handleChangePage={(page) => handleShowLog(jobLogs.id, page)}
          />
        ) : null}
      </>
    </PageLayout>
  );
};
